import { useInfiniteGetList, useNotify } from "react-admin";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { handleHttpError } from "../utils/errorHandler";

export const useInfiniteScroll = (resource, params, options = {}) => {
    const notify = useNotify();
    const observerRef = useRef(null);

    const {
        data: response,
        hasNextPage,
        isFetchingNextPage,
        isPending,
        fetchNextPage,
    } = useInfiniteGetList(resource, params, {
        onError: (error) => {
            handleHttpError(error, notify);
        },
        ...options,
    });

    const handleObserver = useCallback(
        (entries) => {
            const [target] = entries;
            if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage]
    );

    useEffect(() => {
        const element = observerRef.current;
        if (!element) return;
        const observer = new IntersectionObserver(handleObserver, { threshold: 0 });
        observer.observe(element);
        return () => observer.unobserve(element);
    }, [handleObserver]);

    const data = useMemo(
        () => response?.pages?.flatMap((page) => page.data) || [],
        [response?.pages]
    );

    return {
        data,
        isPending,
        isFetchingNextPage,
        hasNextPage,
        observerRef,
    };
};
